import { colorStrings } from "@iewccouk/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import {
    getProductListDataView,
    getProductListDataViewProperty,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Search from "@insite/mobius/Icons/Search";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const pagination = getProductListDataViewProperty(state, "pagination");
    const totalItemCount = pagination?.totalItemCount ?? 0;

    return {
        loaded: !!getProductListDataView(state).value,
        totalItemCount,
    };
};

const mapDispatchToProps = {
    addProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SearchFilteringSearchWithinStyles {
    wrapper?: InjectableCss;
    searchHidden?: HiddenProps;
    searchWrapper?: InjectableCss;
    searchTextField?: TextFieldPresentationProps;
    resultsText?: TypographyPresentationProps;
    resultsCountText?: TypographyPresentationProps;
}

export const searchFilteringSearchWithinStyles: SearchFilteringSearchWithinStyles = {
    wrapper: {
        css: css`
            align-items: center;
            display: flex;
            gap: 24px;
        `,
    },
    searchHidden: {
        css: css`
            width: 100%;
            max-width: 449px;
        `,
    },
    searchWrapper: {
        css: css`
            width: 100%;
        `,
    },
    searchTextField: {
        iconProps: { src: Search },
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
            inputSelect: css`
                background: ${getColor(colorStrings.commonGlobalWhite)};
            `,
        },
    },
    resultsText: {
        weight: 700,
    },
    resultsCountText: {
        weight: 400,
        transform: "lowercase",
        css: css`
            margin-left: 8px;
        `,
    },
};

const styles = searchFilteringSearchWithinStyles;

const SearchFilteringSearchWithinFilter = ({ loaded, totalItemCount, addProductFilters }: Props) => {
    const [searchText, setSearchText] = useState("");

    const doSearch = () => {
        if (searchText.trim() !== "") {
            addProductFilters({ searchWithinQueries: [searchText] });
        }
        setSearchText("");
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            doSearch();
        } else {
            setSearchText(event.currentTarget.value);
        }
    };

    const translation = totalItemCount === 1 ? "1 product" : "{0} products";

    return (
        <StyledWrapper {...styles.wrapper}>
            <Hidden below="md" {...styles.searchHidden}>
                <StyledWrapper {...styles.searchWrapper}>
                    <TextField
                        {...styles.searchTextField}
                        id="productsKeywordSearchFiltering"
                        data-test-selector="searchFilteringSearchWithin"
                        iconClickableProps={{ onClick: doSearch, "aria-roledescription": `Search within` }}
                        value={searchText}
                        onChange={event => setSearchText(event.currentTarget.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={translate("Search")}
                        type="text"
                        disabled={!loaded}
                    />
                </StyledWrapper>
            </Hidden>

            <Typography {...styles.resultsText}>
                {translate("Results")}
                <Typography {...styles.resultsCountText}>{translate(translation, `${totalItemCount}`)}</Typography>
            </Typography>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchFilteringSearchWithinFilter as any) as any;
